<script setup lang="ts">
import { useAnalytic } from "@/stores/analysis";
import { onMounted, ref, watch } from "vue";
import Chart from 'chart.js/auto';
import { useI18n } from "vue-i18n";

const analytic = useAnalytic()
const { t } = useI18n()
const chartRef = ref<HTMLCanvasElement | null>(null);
let myChart: Chart<'bar', number[], string> | null = null;

const labelColors: Record<string, string> = {
  '1': '#25B778', // Scope 1
  '2': '#3800FF', // Scope 2
  '3': '#7243FD', // Scope 3
  'Biogenic Emissions': '#d6675d', // Biogenic EN
  'Emisiones biogénicas': '#d6675d', // Biogenic EN
  'FLAG': '#27ae60'
};


onMounted(async () => {
  generateChart()
})


const processData = () => {
  // Extract unique periods
  const periods = Array.from(new Set(analytic.timeSeries.map(item => item.period)))
    .sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      return numA - numB; // Sort numerically
    })
    .map(item => {
      const numericItem = parseInt(item, 10);
      if (numericItem >= 1 && numericItem <= 12) {
        // Localize for months, fallback to raw value if not found
        const localized = t(`MONTH_${numericItem}`);
        return localized !== `MONTH_${numericItem}` ? localized : item; // Fallback to raw value
      }
      return item; // Return raw value for non-month periods
    });

  // Extract unique labels
  const labels = Array.from(new Set(analytic.timeSeries.map(item => item.label)));

  // Build datasets
  const datasets = labels.map(label => ({
    label: t(`analysis.scope.${label}`),
    data: periods.map(period => {
      const entry = analytic.timeSeries.find(d => {
        const numericPeriod = parseInt(d.period, 10);
        const localizedPeriod =
          numericPeriod >= 1 && numericPeriod <= 12
            ? t(`MONTH_${numericPeriod}`)
            : d.period; // Localize only months
        return localizedPeriod === period && d.label === label;
      });
      // Suppress negative buckets (e.g. Forest-to-Crop removals) — render as 0 instead of a downward bar.
      return entry && entry.value > 0 ? entry.value : 0;
    }),
    backgroundColor: labelColors[label]
  }));

  return { periods, datasets };
};

const generateChart = () => {
  if (chartRef.value) {

    if (myChart) {
      myChart.destroy();
    }

    const { periods, datasets } = processData();
    let isGrouped = analytic.intensity ? true : false;
    myChart = new Chart(chartRef.value, {
      type: 'bar',
      data: {
        labels: periods, // X axis (period)
        datasets // Y axis
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
            position: 'top'
          },
          title: {
            display: false,
            text: 'Stacked Bar Chart (Labels Stacked by Period)'
          }
        },
        scales: {
          x: {
            stacked: !isGrouped
          },
          y: {
            stacked: !isGrouped,
            title: {
              display: true,
              text: analytic.responseUnit
            }
          }
        }
      }
    });
  }
}

watch(() => analytic.timeSeries, (newVal, oldValue) => {
  if (newVal !== oldValue) {
    generateChart();
  }
})

</script>

<template>
  <canvas ref="chartRef" height="400" width="400"></canvas>
</template>